import { useState, useRef, useEffect } from "react";
import * as XLSX from "xlsx";
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	TablePagination,
	TextField,
	Button,
	Typography,
	Box,
	Alert,
	LinearProgress,
	FormControlLabel,
	Switch,
} from "@mui/material";
import HeaderReport from "../../components/header/report";
import { getStatus, sendImageMessage, sendMessage } from "./req/reqMessage";
import Footer from "../../components/footer";
import reqEmpresaCredenciais from "../../services/reqMultiTenant/reqEmpresaCredenciais";
import reqWhatsapp from "../../services/reqMultiTenant/reqWhatsApp";
import { toast, ToastContainer } from "react-toastify";
import InstanceCard from "../register/whatsapp/form/card";
import UploadPhotoV2Image from "../../components/uploading/uploadingPhotoV2image";

const WhatsAppSender = () => {
	const [contacts, setContacts] = useState([]);
	// ✅ AGORA 6 mensagens
	const [messages, setMessages] = useState(["", "", "", "", "", ""]);
	const [status, setStatus] = useState("");
	const [loading, setLoading] = useState(true); // loading geral (carregando credenciais / planilha)
	const [isSending, setIsSending] = useState(false); // envio em andamento
	const [fields, setFields] = useState([]);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(50);

	// controle de tempo entre mensagens
	const [batchSize, setBatchSize] = useState(9); // pausar a cada N mensagens
	const [batchPauseMin, setBatchPauseMin] = useState(4); // duração da pausa em minutos
	const [delayMin, setDelayMin] = useState(36);
	const [delayMax, setDelayMax] = useState(62);
	const [isDelayDisabled, setIsDelayDisabled] = useState(false);
	const [errorLimit, setErrorLimit] = useState(500);

	const [instanciaWhatsapp, setInstanciaWhatsapp] = useState([]);
	// ⚠️ pode ser objeto ou array dependendo do seu backend
	const [credentials, setCredentials] = useState([]);
	const [selectedInstance, setSelectedInstance] = useState(null);

	const [imagePreview, setImagePreview] = useState(null);
	const [isWaiting, setIsWaiting] = useState(false);

	// ✅ alternar envio entre instâncias conectadas
	const [rotateInstances, setRotateInstances] = useState(true);

	// ponteiro round-robin
	const rrIndexRef = useRef(0);

	// pool conectado em ref (mais confiável durante loop do que state)
	const connectedInstancesRef = useRef([]);

	const cancelRef = useRef(false);

	// ✅ AGORA 6 refs (um pra cada textarea)
	const textFieldRefs = [useRef(null), useRef(null), useRef(null), useRef(null), useRef(null), useRef(null)];

	const STATUS_CYCLE = ["📤 Pendente Envio", "🚫 Não enviar", "✅ Enviado"];

	useEffect(() => {
		fetchCredentials();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (instanciaWhatsapp.length > 0 && !selectedInstance) {
			const firstWithPhone = instanciaWhatsapp.find((instance) => instance?.telefone);
			setSelectedInstance(firstWithPhone || instanciaWhatsapp[0]);
		}
	}, [instanciaWhatsapp, selectedInstance]);

	// =========================
	// Helpers
	// =========================

	const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

	const getRandomDelay = (min = delayMin, max = delayMax) => {
		const minVal = Math.max(3, Number(min));
		const maxVal = Math.max(5, Number(max));
		const finalMin = minVal;
		const finalMax = maxVal < minVal ? minVal : maxVal;
		return Math.floor(Math.random() * (finalMax - finalMin + 1) + finalMin) * 1000;
	};

	const getPhoneFieldName = (contact) => {
		return Object.keys(contact).find((key) => key.toLowerCase().includes("telefone")) || "telefone";
	};

	const applyTemplate = (template, contact) => {
		return Object.keys(contact).reduce((msg, field) => {
			const value = contact[field] ?? "";
			return msg.replace(new RegExp(`{${field}}`, "gi"), value);
		}, template);
	};

	// ✅ garante que credenciais funcionem mesmo se vier array
	const getCredObject = () => {
		if (!credentials) return null;
		if (Array.isArray(credentials)) return credentials[0] || null;
		return credentials;
	};

	// ✅ status robusto
	const normalize = (v) =>
		String(v ?? "")
			.trim()
			.toUpperCase();

	const isConnectedStatus = (st) => {
		// 🔎 DIAGNÓSTICO (remova depois se quiser)

		const raw =
			st?.status ??
			st?.state ??
			st?.instance?.status ??
			st?.instance?.state ??
			st?.data?.status ??
			st?.data?.state;

		const s = normalize(raw);

		const CONNECTED_VALUES = new Set([
			"CONNECTED",
			"CONNECTION_OPEN",
			"OPEN",
			"ONLINE",
			"READY",
			"ACTIVE",
			"TRUE",
			"1",
		]);

		const DISCONNECTED_VALUES = new Set([
			"DISCONNECTED",
			"CLOSED",
			"OFFLINE",
			"NOT_FOUND",
			"NOTFOUND",
			"FALSE",
			"0",
			"UNPAIRED",
		]);

		const boolConnected =
			st?.connected === true ||
			st?.isConnected === true ||
			st?.instance?.connected === true ||
			st?.instance?.isConnected === true;

		if (boolConnected) return true;

		if (!s) return false;

		if (DISCONNECTED_VALUES.has(s)) return false;
		if (CONNECTED_VALUES.has(s)) return true;

		// fallback: se não for explicitamente "desconectado", assume conectado
		return true;
	};

	const getCredentialsForInstance = async (instanceObj) => {
		try {
			const credObj = getCredObject();
			const apiKey = credObj?.api_key;
			const url = credObj?.url;
			const instance = instanceObj?.descricao;

			if (!apiKey || !url) throw new Error("Credenciais não carregadas.");
			if (!instance) throw new Error("Nenhuma instância definida.");

			const statusData = await getStatus(url, apiKey, instance);

			if (!statusData) throw new Error("Não foi possível verificar o status da instância.");

			if (!isConnectedStatus(statusData)) {
				throw new Error(`A instância "${instance}" não está conectada.`);
			}

			return { apiKey, url, instance };
		} catch (error) {
			console.error("Erro ao obter credenciais:", error);
			throw error;
		}
	};

	const getConnectedInstances = async () => {
		const credObj = getCredObject();
		const apiKey = credObj?.api_key;
		const url = credObj?.url;

		if (!apiKey || !url) throw new Error("Credenciais não carregadas.");

		const list = (instanciaWhatsapp || []).filter(Boolean);

		const checks = await Promise.all(
			list.map(async (inst) => {
				const instanceName = inst?.descricao;
				if (!instanceName) return null;

				try {
					const st = await getStatus(url, apiKey, instanceName);
					const ok = isConnectedStatus(st);

					// 🔎 DIAGNÓSTICO

					return ok ? inst : null;
				} catch (e) {
					console.warn(`[STATUS ERRO] ${instanceName}:`, e);
					return null;
				}
			}),
		);

		const pool = checks.filter(Boolean);

		return pool;
	};

	const pickInstanceForSend = () => {
		const pool = connectedInstancesRef.current || [];
		if (!pool.length) return null;

		const idx = rrIndexRef.current % pool.length;
		const inst = pool[idx];
		rrIndexRef.current = idx + 1;
		return inst;
	};

	const removeInstanceFromPool = (instanceObj) => {
		if (!instanceObj?.descricao) return;
		const pool = connectedInstancesRef.current || [];
		const nextPool = pool.filter((x) => x?.descricao !== instanceObj.descricao);
		connectedInstancesRef.current = nextPool;

		if (rrIndexRef.current >= nextPool.length) rrIndexRef.current = 0;
	};

	// =========================
	// Data fetch
	// =========================

	const fetchCredentials = async () => {
		try {
			setLoading(true);

			const [empresaCredenciaisRes, whatsappRes] = await Promise.all([
				reqEmpresaCredenciais.listId("whatsapp"),
				reqWhatsapp.listPaginate("descricao", "", "SIM", 1),
			]);

			setCredentials(empresaCredenciaisRes.data);
			setInstanciaWhatsapp(whatsappRes.data);
		} catch (error) {
			toast.warning(error?.message || "Erro ao buscar credenciais");
		} finally {
			setLoading(false);
		}
	};

	// =========================
	// Excel upload / table edits
	// =========================

	const handleFileUpload = (e) => {
		const file = e.target.files?.[0];
		if (!file) return;

		setLoading(true);

		const reader = new FileReader();

		reader.onload = (event) => {
			try {
				const data = new Uint8Array(event.target.result);
				const workbook = XLSX.read(data, { type: "array" });
				const sheetName = workbook.SheetNames[0];
				const worksheet = workbook.Sheets[sheetName];
				const json = XLSX.utils.sheet_to_json(worksheet);

				const formatted = json.map((row) => ({
					...row,
					status: row.status || "📤 Pendente Envio",
				}));

				setContacts(formatted);
				setStatus(`Carregado ${formatted.length} contatos.`);

				if (formatted.length > 0) {
					const detectedFields = Object.keys(formatted[0]).filter((key) => key.toLowerCase() !== "status");
					setFields(detectedFields);
				}
			} catch (err) {
				toast.error("Erro ao ler a planilha.");
				console.error(err);
			}
		};

		reader.onerror = () => {
			toast.error("Erro ao ler o arquivo.");
		};

		reader.onloadend = () => {
			setLoading(false);
			e.target.value = null;
		};

		reader.readAsArrayBuffer(file);
	};

	const handleChange = (index, field, value) => {
		const updated = [...contacts];
		updated[index][field] = value;
		setContacts(updated);
	};

	const toggleStatus = (index) => {
		const updated = [...contacts];
		const current = updated[index].status;
		const nextStatus = STATUS_CYCLE[(STATUS_CYCLE.indexOf(current) + 1) % STATUS_CYCLE.length];
		updated[index].status = nextStatus;
		setContacts(updated);
	};

	// =========================
	// Message composer helpers
	// =========================

	const insertText = (text, index) => {
		setMessages((prev) => {
			const updated = [...prev];
			updated[index] = updated[index] + text + " ";
			return updated;
		});
		setTimeout(() => {
			if (textFieldRefs[index].current) textFieldRefs[index].current.focus();
		}, 0);
	};

	const handleOpenUploadComponent = (e) => {
		e.preventDefault();
		const event = new CustomEvent("upload-whatsapp");
		window.dispatchEvent(event);
	};

	// =========================
	// Export
	// =========================

	const exportToExcel = () => {
		const worksheet = XLSX.utils.json_to_sheet(contacts);
		const workbook = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(workbook, worksheet, "Contatos");
		XLSX.writeFile(workbook, "envio-whatsapp.xlsx");
		setStatus("Arquivo salvo.");
	};

	// =========================
	// Pagination
	// =========================

	const handleChangePage = (event, newPage) => setPage(newPage);

	const handleChangeRowsPerPage = (event) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	// =========================
	// Sending
	// =========================

	const cancelSending = () => {
		cancelRef.current = true;
		setStatus("Pausa envio...");
	};

	const sendMessages = async () => {
		let countError = 0;

		try {
			setIsDelayDisabled(true);
			setIsSending(true);

			const validMessages = messages.filter((msg) => msg.trim() !== "");
			if (validMessages.length === 0) {
				const msg = "Por favor, preencha pelo menos uma mensagem.";
				toast.warning(msg);
				setStatus(msg);
				return;
			}

			if (contacts.length === 0) {
				const msg = "Por favor, carregue contatos.";
				toast.warning(msg);
				setStatus(msg);
				return;
			}

			const credObj = getCredObject();
			if (!credObj?.api_key || !credObj?.url) {
				const msg = "Credenciais não carregadas.";
				toast.error(msg);
				setStatus(msg);
				return;
			}

			// montar pool
			if (rotateInstances) {
				setStatus("🔎 Verificando instâncias conectadas...");
				const pool = await getConnectedInstances();

				if (!pool.length) {
					const msg =
						"Nenhuma instância conectada encontrada para alternância. (Verifique o console do navegador)";
					toast.error(msg);
					setStatus(msg);
					return;
				}

				connectedInstancesRef.current = pool;
				rrIndexRef.current = 0;

				if (!selectedInstance) setSelectedInstance(pool[0]);
			} else {
				if (!selectedInstance) {
					const msg = "Selecione uma instância.";
					toast.warning(msg);
					setStatus(msg);
					return;
				}
				connectedInstancesRef.current = [selectedInstance];
				rrIndexRef.current = 0;
			}

			setStatus("🚀 Iniciando envio das mensagens...");
			cancelRef.current = false;

			const updatedContacts = [...contacts];
			let sentInCurrentBatch = 0;

			for (let i = 0; i < updatedContacts.length; i++) {
				if (countError >= Number(errorLimit)) {
					setStatus("❌ Muitos erros consecutivos. Envio interrompido.");
					toast.error("Muitos erros consecutivos. Envio interrompido.");
					break;
				}

				if (cancelRef.current) {
					setStatus("⛔ Envio cancelado pelo usuário.");
					break;
				}

				const contact = updatedContacts[i];
				if (contact.status !== "📤 Pendente Envio") continue;

				const instanceToUse = rotateInstances ? pickInstanceForSend() : selectedInstance;

				if (!instanceToUse) {
					setStatus("❌ Sem instâncias disponíveis para envio.");
					toast.error("Sem instâncias disponíveis para envio.");
					break;
				}

				let cred;
				try {
					cred = await getCredentialsForInstance(instanceToUse);
				} catch (e) {
					updatedContacts[i].status = "❌ Falhou";
					countError++;
					setContacts([...updatedContacts]);

					if (rotateInstances) {
						removeInstanceFromPool(instanceToUse);
						if (!(connectedInstancesRef.current || []).length) {
							setStatus("❌ Todas as instâncias ficaram indisponíveis. Envio interrompido.");
							toast.error("Todas as instâncias ficaram indisponíveis. Envio interrompido.");
							break;
						}
					}
					continue;
				}

				const phoneField = getPhoneFieldName(contact);

				const messageToSend = validMessages[Math.floor(Math.random() * validMessages.length)];
				const personalizedMessage = applyTemplate(messageToSend, contact);

				try {
					if (imagePreview) {
						await sendImageMessage(
							cred.url,
							cred.apiKey,
							cred.instance,
							contact[phoneField],
							imagePreview,
							personalizedMessage,
						);
					} else {
						await sendMessage(
							cred.url,
							cred.apiKey,
							cred.instance,
							contact[phoneField],
							personalizedMessage,
						);
					}

					setStatus(
						`📨 Enviando via "${cred.instance}" para ${contact.nome || "Sem nome"} (${contact[phoneField]})...`,
					);

					updatedContacts[i].status = "✅ Enviado";
					countError = 0;
				} catch (error) {
					updatedContacts[i].status = "❌ Falhou";
					countError++;

					if (rotateInstances) {
						removeInstanceFromPool(instanceToUse);
						if (!(connectedInstancesRef.current || []).length) {
							setStatus("❌ Todas as instâncias ficaram indisponíveis. Envio interrompido.");
							toast.error("Todas as instâncias ficaram indisponíveis. Envio interrompido.");
							setContacts([...updatedContacts]);
							break;
						}
					}
				}

				setContacts([...updatedContacts]);

				if (i < updatedContacts.length - 1) {
					sentInCurrentBatch++;

					const needsBatchPause =
						Number(batchSize) > 0 && Number(batchPauseMin) > 0 && sentInCurrentBatch >= Number(batchSize);

					if (needsBatchPause) {
						const pauseMs = Number(batchPauseMin) * 60 * 1000;
						setIsWaiting(true);

						const startedAt = Date.now();
						let remaining = pauseMs;

						while (remaining > 0) {
							if (cancelRef.current) {
								setStatus("⛔ Envio cancelado pelo usuário durante a pausa.");
								setIsWaiting(false);
								return;
							}

							const mins = Math.floor(remaining / 60000);
							const secs = Math.ceil((remaining % 60000) / 1000);
							setStatus(`⏸️ Pausa automática: retoma em ${mins}m ${secs}s...`);

							await delay(1000);
							remaining = pauseMs - (Date.now() - startedAt);
						}

						setIsWaiting(false);
						sentInCurrentBatch = 0;
					} else {
						try {
							const waitTime = getRandomDelay();
							setIsWaiting(true);
							setStatus(`Aguardando ${waitTime / 1000}s antes da próxima mensagem...`);
							await delay(waitTime);
						} finally {
							setIsWaiting(false);
						}
					}
				}
			}

			setStatus("✅ Envio de mensagens finalizado.");
		} catch (error) {
			toast.error(error?.message || "Erro ao enviar");
			setStatus(`❌ Erro: ${error?.message || "Erro ao enviar"}`);
		} finally {
			setIsDelayDisabled(false);
			setIsSending(false);
			setIsWaiting(false);
		}
	};

	// =========================
	// UI
	// =========================

	return (
		<>
			<ToastContainer />
			<div className="mt-page" mensagem="Carregando...">
				<Box maxWidth="lg" mx="auto">
					<div className="border-bottom mb-4 mt-4">
						<div className="flex flex-col gap-4">
							<HeaderReport title={"WhatsApp"} subTitle={"Envio de lista"} />
						</div>

						<div className="flex flex-col justify-start items-start w-full md:w-auto">
							{selectedInstance && (
								<InstanceCard
									instanceName={selectedInstance?.descricao || ""}
									instancePhone={selectedInstance?.telefone || ""}
								/>
							)}

							<div className="mt-1 input-select-box w-60 flex flex-col">
								<label className="text-sm">Instância WhatsApp</label>
								<select
									value={selectedInstance ? JSON.stringify(selectedInstance) : ""}
									onChange={(e) => {
										const value = e.target.value;
										if (!value) return;
										setSelectedInstance(value ? JSON.parse(value) : null);
									}}
									className="border rounded p-2 w-full text-sm leading-tight"
									disabled={rotateInstances || isSending}
									title={
										rotateInstances ? "Desative 'Alternar envio' para escolher manualmente." : ""
									}
								>
									<option value="">Selecione a instância</option>
									{instanciaWhatsapp.filter(Boolean).map((instance, index) => (
										<option
											key={instance.id || `${instance.telefone}-${index}`}
											value={JSON.stringify(instance)}
											className="text-sm"
										>
											{instance.telefone
												? `${instance.telefone} - ${instance.descricao}`
												: `${instance.descricao} - Instância não criada`}
										</option>
									))}
								</select>

								<FormControlLabel
									sx={{ mt: 1 }}
									control={
										<Switch
											checked={rotateInstances}
											onChange={(e) => setRotateInstances(e.target.checked)}
											disabled={isSending}
										/>
									}
									label="Alternar envio entre instâncias conectadas"
								/>
							</div>
						</div>
					</div>

					{/* --- Seis caixas de mensagem --- */}
					<Box
						display="flex"
						className="overflow-auto"
						gap={3}
						mb={1}
						sx={{
							overflowX: "auto",
							overflowY: "hidden",
							WebkitOverflowScrolling: "touch",
							pb: 1,
						}}
						flexWrap="nowrap"
					>
						{messages.map((_, idx) => (
							<Box
								key={idx}
								sx={{
									minWidth: 380,
									maxWidth: 520,
									flex: "0 0 auto",
								}}
							>
								<Typography variant="subtitle1" gutterBottom>
									Mensagem {idx + 1}:
								</Typography>

								<TextField
									inputRef={textFieldRefs[idx]}
									multiline
									rows={14}
									value={messages[idx]}
									onChange={(e) => {
										const updated = [...messages];
										updated[idx] = e.target.value;
										setMessages(updated);
									}}
									placeholder={`Digite a mensagem ${idx + 1}`}
									fullWidth
								/>

								<Box mt={1} display="flex" flexWrap="wrap" gap={1}>
									<Button size="small" onClick={() => insertText("*texto*", idx)}>
										*Negrito*
									</Button>
									<Button size="small" onClick={() => insertText("_texto_", idx)}>
										_Itálico_
									</Button>
									<Button size="small" onClick={() => insertText("~texto~", idx)}>
										~Riscado~
									</Button>
									<Button size="small" onClick={() => insertText("`texto`", idx)}>
										`Monoespaçado`
									</Button>

									{fields.map((field) => (
										<Button key={field} size="small" onClick={() => insertText(`{${field}}`, idx)}>
											{`{${field}}`}
										</Button>
									))}
								</Box>
							</Box>
						))}
					</Box>

					{/* Controles */}
					<Box className="flex justify-between flex-wrap" mb={3}>
						<Box display="flex" gap={2} alignItems="center" mb={2}>
							<TextField
								label="Delay mínimo em segundos"
								type="number"
								value={delayMin}
								onChange={(e) => setDelayMin(Number(e.target.value))}
								size="small"
								inputProps={{ min: 3 }}
								disabled={isDelayDisabled}
							/>
							<TextField
								label="Delay máximo em segundos"
								type="number"
								value={delayMax}
								onChange={(e) => setDelayMax(Number(e.target.value))}
								size="small"
								inputProps={{ min: delayMin }}
								disabled={isDelayDisabled}
							/>
						</Box>

						<Box display="flex" gap={2} alignItems="center" mb={2}>
							<TextField
								label="Pausar após (qtd. de mensagens)"
								type="number"
								value={batchSize}
								onChange={(e) => setBatchSize(Math.max(1, Number(e.target.value)))}
								size="small"
								inputProps={{ min: 10 }}
								disabled={isDelayDisabled}
							/>
							<TextField
								label="Tempo da pausa (minutos)"
								type="number"
								value={batchPauseMin}
								onChange={(e) => setBatchPauseMin(Math.max(1, Number(e.target.value)))}
								size="small"
								inputProps={{ min: 1 }}
								disabled={isDelayDisabled}
							/>
						</Box>

						<Box display="flex" gap={2} alignItems="center" mb={2}>
							<TextField
								label="Máx. erros consecutivos"
								type="number"
								value={errorLimit}
								onChange={(e) => setErrorLimit(Math.max(1, Number(e.target.value)))}
								size="small"
								inputProps={{ min: 1 }}
								disabled={isDelayDisabled}
							/>
						</Box>

						<Button
							variant="contained"
							className="h-8"
							onClick={() => {
								setMessages((prev) => prev.map(() => prev[0]));
								setTimeout(() => {
									if (textFieldRefs[1].current) textFieldRefs[1].current.focus();
								}, 0);
							}}
						>
							Clonar mensagem
						</Button>
					</Box>

					{/* Imagem */}
					<div>
						{imagePreview && (
							<div className="relative h-72 rounded-lg overflow-hidden ">
								<img
									src={imagePreview}
									alt=""
									className="absolute inset-0 w-full h-full object-contain rounded-lg shadow-lg"
								/>
							</div>
						)}

						<button
							className="btn btn-sm btn-outline-primary mt-3 w-56 me-5"
							onClick={handleOpenUploadComponent}
						>
							Escolher imagem
						</button>

						<button
							className="btn btn-sm btn-outline-danger mt-3 w-56"
							onClick={() => setImagePreview(null)}
							hidden={!imagePreview}
						>
							Limpar imagem
						</button>
					</div>

					<Button variant="contained" className="w-48 h-8 mt-4" component="label">
						Carregar Planilha
						<input type="file" accept=".xlsx, .xls" hidden onChange={handleFileUpload} />
					</Button>

					{contacts.length > 0 && (
						<Paper sx={{ mt: 3 }}>
							<Typography variant="h6" p={2}>
								Contatos
							</Typography>

							<TableContainer>
								<Table>
									<TableHead className="color-table">
										<TableRow>
											{Object.keys(contacts[0])
												.filter((col) => col !== "status")
												.map((col) => (
													<TableCell key={col} style={{ color: "#fff" }}>
														{col.toUpperCase()}
													</TableCell>
												))}
											<TableCell style={{ color: "#fff" }}>Status</TableCell>
										</TableRow>
									</TableHead>

									{(loading || isSending) && (
										<TableRow>
											<TableCell colSpan={Object.keys(contacts[0]).length} sx={{ p: 0 }}>
												<LinearProgress color="primary" />
											</TableCell>
										</TableRow>
									)}

									<TableBody>
										{contacts
											.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
											.map((c, i) => (
												<TableRow key={i}>
													{Object.keys(c)
														.filter((col) => col !== "status")
														.map((col) => (
															<TableCell key={col}>
																<TextField
																	value={c[col]}
																	onChange={(e) =>
																		handleChange(
																			page * rowsPerPage + i,
																			col,
																			e.target.value,
																		)
																	}
																	size="small"
																	fullWidth
																/>
															</TableCell>
														))}
													<TableCell
														onClick={() => toggleStatus(page * rowsPerPage + i)}
														sx={{
															cursor: "pointer",
															color:
																c.status === "📤 Pendente Envio"
																	? "primary.main"
																	: c.status === "🚫 Não enviar"
																		? "text.secondary"
																		: c.status === "✅ Enviado"
																			? "success.main"
																			: "error.main",
														}}
													>
														{c.status}
													</TableCell>
												</TableRow>
											))}
									</TableBody>
								</Table>
							</TableContainer>

							<TablePagination
								component="div"
								count={contacts.length}
								page={page}
								onPageChange={handleChangePage}
								rowsPerPage={rowsPerPage}
								onRowsPerPageChange={handleChangeRowsPerPage}
								labelRowsPerPage="Linhas por página"
							/>

							{status && (
								<div className="flex items-center">
									{isWaiting && (
										<div>
											<span
												className="spinner-grow spinner-grow-sm text-primary ms-2"
												role="status"
											></span>
										</div>
									)}
									<Typography p={2}>{status}</Typography>
								</div>
							)}

							<Box display="flex" justifyContent="space-between" p={2}>
								<Box display="flex" gap={2}>
									<Button
										variant="contained"
										color="primary"
										onClick={sendMessages}
										disabled={isSending || loading}
									>
										{isSending ? "Enviando..." : "Enviar Mensagens"}
									</Button>
									<Button
										variant="contained"
										color="error"
										onClick={cancelSending}
										disabled={!isSending}
									>
										Pausar Envio
									</Button>
								</Box>

								<Button
									variant="contained"
									color="success"
									onClick={exportToExcel}
									disabled={contacts.length === 0 || isDelayDisabled || isSending}
								>
									Salvar Cópia
								</Button>
							</Box>
						</Paper>
					)}

					<div>
						<Alert severity="warning" sx={{ mt: 3 }}>
							⚠️ <strong>Atenção:</strong> O envio de mensagens em massa pelo WhatsApp pode violar as
							políticas do serviço e resultar no bloqueio temporário ou permanente da sua conta. Use com
							responsabilidade e moderação.
						</Alert>
					</div>

					<UploadPhotoV2Image
						ident="upload-whatsapp"
						nameImg="whatsapp"
						id={123}
						onChange={({ url }) => setImagePreview(url)}
						type="logo"
					/>

					<Footer />
				</Box>
			</div>
		</>
	);
};

export default WhatsAppSender;

// import { useState, useRef, useEffect } from "react";
// import * as XLSX from "xlsx";
// import {
// 	Table,
// 	TableBody,
// 	TableCell,
// 	TableContainer,
// 	TableHead,
// 	TableRow,
// 	Paper,
// 	TablePagination,
// 	TextField,
// 	Button,
// 	Typography,
// 	Box,
// 	Alert,
// 	LinearProgress,
// 	FormControlLabel,
// 	Switch,
// } from "@mui/material";
// import HeaderReport from "../../components/header/report";
// import { getStatus, sendImageMessage, sendMessage } from "./req/reqMessage";
// import Footer from "../../components/footer";
// import reqEmpresaCredenciais from "../../services/reqMultiTenant/reqEmpresaCredenciais";
// import reqWhatsapp from "../../services/reqMultiTenant/reqWhatsApp";
// import { toast, ToastContainer } from "react-toastify";
// import InstanceCard from "../register/whatsapp/form/card";
// import UploadPhotoV2Image from "../../components/uploading/uploadingPhotoV2image";

// const WhatsAppSender = () => {
// 	const [contacts, setContacts] = useState([]);
// 	const [messages, setMessages] = useState(["", "", ""]);
// 	const [status, setStatus] = useState("");
// 	const [loading, setLoading] = useState(true); // loading geral (carregando credenciais / planilha)
// 	const [isSending, setIsSending] = useState(false); // envio em andamento
// 	const [fields, setFields] = useState([]);
// 	const [page, setPage] = useState(0);
// 	const [rowsPerPage, setRowsPerPage] = useState(50);

// 	// controle de tempo entre mensagens
// 	const [batchSize, setBatchSize] = useState(9); // pausar a cada N mensagens
// 	const [batchPauseMin, setBatchPauseMin] = useState(4); // duração da pausa em minutos
// 	const [delayMin, setDelayMin] = useState(36);
// 	const [delayMax, setDelayMax] = useState(62);
// 	const [isDelayDisabled, setIsDelayDisabled] = useState(false);
// 	const [errorLimit, setErrorLimit] = useState(500);

// 	const [instanciaWhatsapp, setInstanciaWhatsapp] = useState([]);
// 	const [credentials, setCredentials] = useState([]);
// 	const [selectedInstance, setSelectedInstance] = useState(null);

// 	const [imagePreview, setImagePreview] = useState(null);
// 	const [isWaiting, setIsWaiting] = useState(false);

// 	// ✅ NOVO: alternar envio entre instâncias conectadas
// 	const [rotateInstances, setRotateInstances] = useState(true);

// 	// ponteiro round-robin
// 	const rrIndexRef = useRef(0);

// 	// pool conectado em ref (mais confiável durante loop do que state)
// 	const connectedInstancesRef = useRef([]);

// 	const cancelRef = useRef(false);

// 	// Para focar cada textarea individualmente
// 	const textFieldRefs = [useRef(null), useRef(null), useRef(null)];

// 	const STATUS_CYCLE = ["📤 Pendente Envio", "🚫 Não enviar", "✅ Enviado"];

// 	useEffect(() => {
// 		fetchCredentials();
// 		// eslint-disable-next-line react-hooks/exhaustive-deps
// 	}, []);

// 	useEffect(() => {
// 		// seleção inicial só para modo manual / exibição
// 		if (instanciaWhatsapp.length > 0 && !selectedInstance) {
// 			const firstWithPhone = instanciaWhatsapp.find((instance) => instance?.telefone);
// 			setSelectedInstance(firstWithPhone || instanciaWhatsapp[0]);
// 		}
// 	}, [instanciaWhatsapp, selectedInstance]);

// 	// =========================
// 	// Helpers
// 	// =========================

// 	const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// 	const getRandomDelay = (min = delayMin, max = delayMax) => {
// 		const minVal = Math.max(3, Number(min));
// 		const maxVal = Math.max(5, Number(max));
// 		const finalMin = minVal;
// 		const finalMax = maxVal < minVal ? minVal : maxVal;
// 		return Math.floor(Math.random() * (finalMax - finalMin + 1) + finalMin) * 1000;
// 	};

// 	const getPhoneFieldName = (contact) => {
// 		return Object.keys(contact).find((key) => key.toLowerCase().includes("telefone")) || "telefone";
// 	};

// 	const applyTemplate = (template, contact) => {
// 		return Object.keys(contact).reduce((msg, field) => {
// 			const value = contact[field] ?? "";
// 			return msg.replace(new RegExp(`{${field}}`, "gi"), value);
// 		}, template);
// 	};

// 	const getCredentialsForInstance = async (instanceObj) => {
// 		try {
// 			const apiKey = credentials?.api_key;
// 			const url = credentials?.url;
// 			const instance = instanceObj?.descricao;

// 			if (!apiKey || !url) throw new Error("Credenciais não carregadas.");
// 			if (!instance) throw new Error("Nenhuma instância definida.");

// 			// Verificar status da instância antes de retornar
// 			const statusData = await getStatus(url, apiKey, instance);

// 			if (!statusData) throw new Error("Não foi possível verificar o status da instância.");

// 			if (statusData.status === "DISCONNECTED") {
// 				throw new Error(`A instância "${instance}" está desconectada.`);
// 			}

// 			if (statusData.status === "NOT_FOUND") {
// 				throw new Error(`A instância "${instance}" não existe.`);
// 			}

// 			return { apiKey, url, instance };
// 		} catch (error) {
// 			console.error("Erro ao obter credenciais:", error);
// 			throw error;
// 		}
// 	};

// 	const isConnectedStatus = (st) => {
// 		// Ajuste aqui se sua API tiver um status "CONNECTED" explícito.
// 		// Por enquanto, considera conectada se não for DISCONNECTED/NOT_FOUND e existir st.status.
// 		if (!st?.status) return false;
// 		if (st.status === "DISCONNECTED") return false;
// 		if (st.status === "NOT_FOUND") return false;
// 		return true;
// 	};

// 	const getConnectedInstances = async () => {
// 		const apiKey = credentials?.api_key;
// 		const url = credentials?.url;

// 		if (!apiKey || !url) throw new Error("Credenciais não carregadas.");

// 		const checks = await Promise.all(
// 			(instanciaWhatsapp || []).filter(Boolean).map(async (inst) => {
// 				try {
// 					const instanceName = inst?.descricao;
// 					if (!instanceName) return null;
// 					const st = await getStatus(url, apiKey, instanceName);
// 					return isConnectedStatus(st) ? inst : null;
// 				} catch {
// 					return null;
// 				}
// 			}),
// 		);

// 		return checks.filter(Boolean);
// 	};

// 	const pickInstanceForSend = () => {
// 		const pool = connectedInstancesRef.current || [];
// 		if (!pool.length) return null;

// 		const idx = rrIndexRef.current % pool.length;
// 		const inst = pool[idx];
// 		rrIndexRef.current = idx + 1;
// 		return inst;
// 	};

// 	const removeInstanceFromPool = (instanceObj) => {
// 		if (!instanceObj?.descricao) return;
// 		const pool = connectedInstancesRef.current || [];
// 		const nextPool = pool.filter((x) => x?.descricao !== instanceObj.descricao);
// 		connectedInstancesRef.current = nextPool;

// 		// se o ponteiro estiver fora do range, ajusta
// 		if (rrIndexRef.current >= nextPool.length) rrIndexRef.current = 0;
// 	};

// 	// =========================
// 	// Data fetch
// 	// =========================

// 	const fetchCredentials = async () => {
// 		try {
// 			setLoading(true);

// 			const [empresaCredenciaisRes, whatsappRes] = await Promise.all([
// 				reqEmpresaCredenciais.listId("whatsapp"),
// 				reqWhatsapp.listPaginate("descricao", "", "SIM", 1),
// 			]);

// 			setCredentials(empresaCredenciaisRes.data);
// 			setInstanciaWhatsapp(whatsappRes.data);
// 		} catch (error) {
// 			toast.warning(error?.message || "Erro ao buscar credenciais");
// 		} finally {
// 			setLoading(false);
// 		}
// 	};

// 	// =========================
// 	// Excel upload / table edits
// 	// =========================

// 	const handleFileUpload = (e) => {
// 		const file = e.target.files?.[0];
// 		if (!file) return;

// 		setLoading(true);

// 		const reader = new FileReader();

// 		reader.onload = (event) => {
// 			try {
// 				const data = new Uint8Array(event.target.result);
// 				const workbook = XLSX.read(data, { type: "array" });
// 				const sheetName = workbook.SheetNames[0];
// 				const worksheet = workbook.Sheets[sheetName];
// 				const json = XLSX.utils.sheet_to_json(worksheet);

// 				const formatted = json.map((row) => ({
// 					...row,
// 					status: row.status || "📤 Pendente Envio",
// 				}));

// 				setContacts(formatted);
// 				setStatus(`Carregado ${formatted.length} contatos.`);

// 				if (formatted.length > 0) {
// 					const detectedFields = Object.keys(formatted[0]).filter((key) => key.toLowerCase() !== "status");
// 					setFields(detectedFields);
// 				}
// 			} catch (err) {
// 				toast.error("Erro ao ler a planilha.");
// 				console.error(err);
// 			}
// 		};

// 		reader.onerror = () => {
// 			toast.error("Erro ao ler o arquivo.");
// 		};

// 		reader.onloadend = () => {
// 			setLoading(false);
// 			// resetar input para permitir reupload do mesmo arquivo
// 			e.target.value = null;
// 		};

// 		reader.readAsArrayBuffer(file);
// 	};

// 	const handleChange = (index, field, value) => {
// 		const updated = [...contacts];
// 		updated[index][field] = value;
// 		setContacts(updated);
// 	};

// 	const toggleStatus = (index) => {
// 		const updated = [...contacts];
// 		const current = updated[index].status;
// 		const nextStatus = STATUS_CYCLE[(STATUS_CYCLE.indexOf(current) + 1) % STATUS_CYCLE.length];
// 		updated[index].status = nextStatus;
// 		setContacts(updated);
// 	};

// 	// =========================
// 	// Message composer helpers
// 	// =========================

// 	const insertText = (text, index) => {
// 		setMessages((prev) => {
// 			const updated = [...prev];
// 			updated[index] = updated[index] + text + " ";
// 			return updated;
// 		});
// 		setTimeout(() => {
// 			if (textFieldRefs[index].current) textFieldRefs[index].current.focus();
// 		}, 0);
// 	};

// 	const handleOpenUploadComponent = (e) => {
// 		e.preventDefault();
// 		const event = new CustomEvent("upload-whatsapp");
// 		window.dispatchEvent(event);
// 	};

// 	// =========================
// 	// Export
// 	// =========================

// 	const exportToExcel = () => {
// 		const worksheet = XLSX.utils.json_to_sheet(contacts);
// 		const workbook = XLSX.utils.book_new();
// 		XLSX.utils.book_append_sheet(workbook, worksheet, "Contatos");
// 		XLSX.writeFile(workbook, "envio-whatsapp.xlsx");
// 		setStatus("Arquivo salvo.");
// 	};

// 	// =========================
// 	// Pagination
// 	// =========================

// 	const handleChangePage = (event, newPage) => setPage(newPage);

// 	const handleChangeRowsPerPage = (event) => {
// 		setRowsPerPage(parseInt(event.target.value, 10));
// 		setPage(0);
// 	};

// 	// =========================
// 	// Sending
// 	// =========================

// 	const cancelSending = () => {
// 		cancelRef.current = true;
// 		setStatus("Pausa envio...");
// 	};

// 	const sendMessages = async () => {
// 		let countError = 0;

// 		try {
// 			setIsDelayDisabled(true);
// 			setIsSending(true);

// 			// Filtra apenas mensagens preenchidas
// 			const validMessages = messages.filter((msg) => msg.trim() !== "");
// 			if (validMessages.length === 0) {
// 				const msg = "Por favor, preencha pelo menos uma mensagem.";
// 				toast.warning(msg);
// 				setStatus(msg);
// 				return;
// 			}

// 			if (contacts.length === 0) {
// 				const msg = "Por favor, carregue contatos.";
// 				toast.warning(msg);
// 				setStatus(msg);
// 				return;
// 			}

// 			if (!credentials?.api_key || !credentials?.url) {
// 				const msg = "Credenciais não carregadas.";
// 				toast.error(msg);
// 				setStatus(msg);
// 				return;
// 			}

// 			// montar pool
// 			if (rotateInstances) {
// 				setStatus("🔎 Verificando instâncias conectadas...");
// 				const pool = await getConnectedInstances();

// 				if (!pool.length) {
// 					const msg = "Nenhuma instância conectada encontrada para alternância.";
// 					toast.error(msg);
// 					setStatus(msg);
// 					return;
// 				}

// 				connectedInstancesRef.current = pool;
// 				rrIndexRef.current = 0;

// 				// se a selecionada não existe ou está nula, só para exibição no card
// 				if (!selectedInstance) setSelectedInstance(pool[0]);
// 			} else {
// 				// modo manual
// 				if (!selectedInstance) {
// 					const msg = "Selecione uma instância.";
// 					toast.warning(msg);
// 					setStatus(msg);
// 					return;
// 				}
// 				connectedInstancesRef.current = [selectedInstance];
// 				rrIndexRef.current = 0;
// 			}

// 			setStatus("🚀 Iniciando envio das mensagens...");
// 			cancelRef.current = false;

// 			const updatedContacts = [...contacts];
// 			let sentInCurrentBatch = 0;

// 			for (let i = 0; i < updatedContacts.length; i++) {
// 				// trava por limite de erros
// 				if (countError >= Number(errorLimit)) {
// 					setStatus("❌ Muitos erros consecutivos. Envio interrompido.");
// 					toast.error("Muitos erros consecutivos. Envio interrompido.");
// 					break;
// 				}

// 				if (cancelRef.current) {
// 					setStatus("⛔ Envio cancelado pelo usuário.");
// 					break;
// 				}

// 				const contact = updatedContacts[i];
// 				if (contact.status !== "📤 Pendente Envio") continue;

// 				// escolhe a instância
// 				const instanceToUse = rotateInstances ? pickInstanceForSend() : selectedInstance;

// 				if (!instanceToUse) {
// 					setStatus("❌ Sem instâncias disponíveis para envio.");
// 					toast.error("Sem instâncias disponíveis para envio.");
// 					break;
// 				}

// 				// pega credenciais e valida status (também ajuda a detectar desconexão no meio)
// 				let cred;
// 				try {
// 					cred = await getCredentialsForInstance(instanceToUse);
// 				} catch (e) {
// 					updatedContacts[i].status = "❌ Falhou";
// 					countError++;
// 					setContacts([...updatedContacts]);

// 					if (rotateInstances) {
// 						removeInstanceFromPool(instanceToUse);
// 						if (!(connectedInstancesRef.current || []).length) {
// 							setStatus("❌ Todas as instâncias ficaram indisponíveis. Envio interrompido.");
// 							toast.error("Todas as instâncias ficaram indisponíveis. Envio interrompido.");
// 							break;
// 						}
// 					}
// 					continue;
// 				}

// 				const phoneField = getPhoneFieldName(contact);

// 				// sorteia mensagem diferente para cada contato
// 				const messageToSend = validMessages[Math.floor(Math.random() * validMessages.length)];
// 				const personalizedMessage = applyTemplate(messageToSend, contact);

// 				try {
// 					if (imagePreview) {
// 						await sendImageMessage(
// 							cred.url,
// 							cred.apiKey,
// 							cred.instance,
// 							contact[phoneField],
// 							imagePreview,
// 							personalizedMessage,
// 						);
// 					} else {
// 						await sendMessage(
// 							cred.url,
// 							cred.apiKey,
// 							cred.instance,
// 							contact[phoneField],
// 							personalizedMessage,
// 						);
// 					}

// 					setStatus(
// 						`📨 Enviando via "${cred.instance}" para ${contact.nome || "Sem nome"} (${contact[phoneField]})...`,
// 					);

// 					updatedContacts[i].status = "✅ Enviado";
// 					countError = 0;
// 				} catch (error) {
// 					updatedContacts[i].status = "❌ Falhou";
// 					countError++;

// 					// se falhar e estiver alternando, tira a instância do pool para não insistir
// 					if (rotateInstances) {
// 						removeInstanceFromPool(instanceToUse);
// 						if (!(connectedInstancesRef.current || []).length) {
// 							setStatus("❌ Todas as instâncias ficaram indisponíveis. Envio interrompido.");
// 							toast.error("Todas as instâncias ficaram indisponíveis. Envio interrompido.");
// 							setContacts([...updatedContacts]);
// 							break;
// 						}
// 					}
// 				}

// 				setContacts([...updatedContacts]);

// 				// delays e pausas (somente se ainda tem próximo envio)
// 				if (i < updatedContacts.length - 1) {
// 					sentInCurrentBatch++;

// 					const needsBatchPause =
// 						Number(batchSize) > 0 && Number(batchPauseMin) > 0 && sentInCurrentBatch >= Number(batchSize);

// 					if (needsBatchPause) {
// 						const pauseMs = Number(batchPauseMin) * 60 * 1000;
// 						setIsWaiting(true);

// 						const startedAt = Date.now();
// 						let remaining = pauseMs;

// 						while (remaining > 0) {
// 							if (cancelRef.current) {
// 								setStatus("⛔ Envio cancelado pelo usuário durante a pausa.");
// 								setIsWaiting(false);
// 								return;
// 							}

// 							const mins = Math.floor(remaining / 60000);
// 							const secs = Math.ceil((remaining % 60000) / 1000);
// 							setStatus(`⏸️ Pausa automática: retoma em ${mins}m ${secs}s...`);

// 							await delay(1000);
// 							remaining = pauseMs - (Date.now() - startedAt);
// 						}

// 						setIsWaiting(false);
// 						sentInCurrentBatch = 0;
// 					} else {
// 						try {
// 							const waitTime = getRandomDelay();
// 							setIsWaiting(true);
// 							setStatus(`Aguardando ${waitTime / 1000}s antes da próxima mensagem...`);
// 							await delay(waitTime);
// 						} finally {
// 							setIsWaiting(false);
// 						}
// 					}
// 				}
// 			}

// 			setStatus("✅ Envio de mensagens finalizado.");
// 		} catch (error) {
// 			toast.error(error?.message || "Erro ao enviar");
// 			setStatus(`❌ Erro: ${error?.message || "Erro ao enviar"}`);
// 		} finally {
// 			setIsDelayDisabled(false);
// 			setIsSending(false);
// 			setIsWaiting(false);
// 		}
// 	};

// 	// =========================
// 	// UI
// 	// =========================

// 	return (
// 		<>
// 			<ToastContainer />
// 			<div className="mt-page" mensagem="Carregando...">
// 				<Box maxWidth="lg" mx="auto">
// 					<div className="border-bottom mb-4 mt-4">
// 						<div className="flex flex-col gap-4">
// 							<HeaderReport title={"WhatsApp"} subTitle={"Envio de lista"} />
// 						</div>

// 						<div className="flex flex-col justify-start items-start w-full md:w-auto">
// 							{selectedInstance && (
// 								<InstanceCard
// 									instanceName={selectedInstance?.descricao || ""}
// 									instancePhone={selectedInstance?.telefone || ""}
// 								/>
// 							)}

// 							<div className="mt-1 input-select-box w-60 flex flex-col">
// 								<label className="text-sm">Instância WhatsApp</label>
// 								<select
// 									value={selectedInstance ? JSON.stringify(selectedInstance) : ""}
// 									onChange={(e) => {
// 										const value = e.target.value;
// 										if (!value) return;
// 										setSelectedInstance(value ? JSON.parse(value) : null);
// 									}}
// 									className="border rounded p-2 w-full text-sm leading-tight"
// 									disabled={rotateInstances || isSending}
// 									title={
// 										rotateInstances ? "Desative 'Alternar envio' para escolher manualmente." : ""
// 									}
// 								>
// 									<option value="">Selecione a instância</option>
// 									{instanciaWhatsapp.filter(Boolean).map((instance, index) => (
// 										<option
// 											key={instance.id || `${instance.telefone}-${index}`}
// 											value={JSON.stringify(instance)}
// 											className="text-sm"
// 										>
// 											{instance.telefone
// 												? `${instance.telefone} - ${instance.descricao}`
// 												: `${instance.descricao} - Instância não criada`}
// 										</option>
// 									))}
// 								</select>

// 								<FormControlLabel
// 									sx={{ mt: 1 }}
// 									control={
// 										<Switch
// 											checked={rotateInstances}
// 											onChange={(e) => setRotateInstances(e.target.checked)}
// 											disabled={isSending}
// 										/>
// 									}
// 									label="Alternar envio entre instâncias conectadas"
// 								/>
// 							</div>
// 						</div>
// 					</div>

// 					{/* --- Três caixas de mensagem --- */}
// 					<Box display="flex" className="overflow-auto" gap={3} mb={1}>
// 						{[0, 1, 2].map((idx) => (
// 							<Box key={idx} flex={1}>
// 								<Typography variant="subtitle1" gutterBottom>
// 									Mensagem {idx + 1}:
// 								</Typography>
// 								<TextField
// 									inputRef={textFieldRefs[idx]}
// 									multiline
// 									rows={14}
// 									value={messages[idx]}
// 									onChange={(e) => {
// 										const updated = [...messages];
// 										updated[idx] = e.target.value;
// 										setMessages(updated);
// 									}}
// 									placeholder={`Digite a mensagem ${idx + 1}`}
// 									fullWidth
// 								/>
// 								<Box mt={1} display="flex" flexWrap="wrap" gap={1}>
// 									<Button size="small" onClick={() => insertText("*texto*", idx)}>
// 										*Negrito*
// 									</Button>
// 									<Button size="small" onClick={() => insertText("_texto_", idx)}>
// 										_Itálico_
// 									</Button>
// 									<Button size="small" onClick={() => insertText("~texto~", idx)}>
// 										~Riscado~
// 									</Button>
// 									<Button size="small" onClick={() => insertText("`texto`", idx)}>
// 										`Monoespaçado`
// 									</Button>
// 									{fields.map((field) => (
// 										<Button key={field} size="small" onClick={() => insertText(`{${field}}`, idx)}>
// 											{`{${field}}`}
// 										</Button>
// 									))}
// 								</Box>
// 							</Box>
// 						))}
// 					</Box>

// 					{/* Controles */}
// 					<Box className="flex justify-between flex-wrap" mb={3}>
// 						<Box display="flex" gap={2} alignItems="center" mb={2}>
// 							<TextField
// 								label="Delay mínimo em segundos"
// 								type="number"
// 								value={delayMin}
// 								onChange={(e) => setDelayMin(Number(e.target.value))}
// 								size="small"
// 								inputProps={{ min: 3 }}
// 								disabled={isDelayDisabled}
// 							/>
// 							<TextField
// 								label="Delay máximo em segundos"
// 								type="number"
// 								value={delayMax}
// 								onChange={(e) => setDelayMax(Number(e.target.value))}
// 								size="small"
// 								inputProps={{ min: delayMin }}
// 								disabled={isDelayDisabled}
// 							/>
// 						</Box>

// 						<Box display="flex" gap={2} alignItems="center" mb={2}>
// 							<TextField
// 								label="Pausar após (qtd. de mensagens)"
// 								type="number"
// 								value={batchSize}
// 								onChange={(e) => setBatchSize(Math.max(1, Number(e.target.value)))}
// 								size="small"
// 								inputProps={{ min: 10 }}
// 								disabled={isDelayDisabled}
// 							/>
// 							<TextField
// 								label="Tempo da pausa (minutos)"
// 								type="number"
// 								value={batchPauseMin}
// 								onChange={(e) => setBatchPauseMin(Math.max(1, Number(e.target.value)))}
// 								size="small"
// 								inputProps={{ min: 1 }}
// 								disabled={isDelayDisabled}
// 							/>
// 						</Box>

// 						<Box display="flex" gap={2} alignItems="center" mb={2}>
// 							<TextField
// 								label="Máx. erros consecutivos"
// 								type="number"
// 								value={errorLimit}
// 								onChange={(e) => setErrorLimit(Math.max(1, Number(e.target.value)))}
// 								size="small"
// 								inputProps={{ min: 1 }}
// 								disabled={isDelayDisabled}
// 							/>
// 						</Box>

// 						<Button
// 							variant="contained"
// 							className="h-8"
// 							onClick={() => {
// 								setMessages((prev) => [prev[0], prev[0], prev[0]]);
// 								setTimeout(() => {
// 									if (textFieldRefs[1].current) textFieldRefs[1].current.focus();
// 								}, 0);
// 							}}
// 						>
// 							Clonar mensagem
// 						</Button>
// 					</Box>

// 					{/* Imagem */}
// 					<div>
// 						{imagePreview && (
// 							<div className="relative h-72 rounded-lg overflow-hidden ">
// 								<img
// 									src={imagePreview}
// 									alt=""
// 									className="absolute inset-0 w-full h-full object-contain rounded-lg shadow-lg"
// 								/>
// 							</div>
// 						)}

// 						<button
// 							className="btn btn-sm btn-outline-primary mt-3 w-56 me-5"
// 							onClick={handleOpenUploadComponent}
// 						>
// 							Escolher imagem
// 						</button>

// 						<button
// 							className="btn btn-sm btn-outline-danger mt-3 w-56"
// 							onClick={() => setImagePreview(null)}
// 							hidden={!imagePreview}
// 						>
// 							Limpar imagem
// 						</button>
// 					</div>

// 					<Button variant="contained" className="w-48 h-8 mt-4" component="label">
// 						Carregar Planilha
// 						<input type="file" accept=".xlsx, .xls" hidden onChange={handleFileUpload} />
// 					</Button>

// 					{contacts.length > 0 && (
// 						<Paper sx={{ mt: 3 }}>
// 							<Typography variant="h6" p={2}>
// 								Contatos
// 							</Typography>

// 							<TableContainer>
// 								<Table>
// 									<TableHead className="color-table">
// 										<TableRow>
// 											{Object.keys(contacts[0])
// 												.filter((col) => col !== "status")
// 												.map((col) => (
// 													<TableCell key={col} style={{ color: "#fff" }}>
// 														{col.toUpperCase()}
// 													</TableCell>
// 												))}
// 											<TableCell style={{ color: "#fff" }}>Status</TableCell>
// 										</TableRow>
// 									</TableHead>

// 									{(loading || isSending) && (
// 										<TableRow>
// 											<TableCell colSpan={Object.keys(contacts[0]).length} sx={{ p: 0 }}>
// 												<LinearProgress color="primary" />
// 											</TableCell>
// 										</TableRow>
// 									)}

// 									<TableBody>
// 										{contacts
// 											.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
// 											.map((c, i) => (
// 												<TableRow key={i}>
// 													{Object.keys(c)
// 														.filter((col) => col !== "status")
// 														.map((col) => (
// 															<TableCell key={col}>
// 																<TextField
// 																	value={c[col]}
// 																	onChange={(e) =>
// 																		handleChange(
// 																			page * rowsPerPage + i,
// 																			col,
// 																			e.target.value,
// 																		)
// 																	}
// 																	size="small"
// 																	fullWidth
// 																/>
// 															</TableCell>
// 														))}
// 													<TableCell
// 														onClick={() => toggleStatus(page * rowsPerPage + i)}
// 														sx={{
// 															cursor: "pointer",
// 															color:
// 																c.status === "📤 Pendente Envio"
// 																	? "primary.main"
// 																	: c.status === "🚫 Não enviar"
// 																		? "text.secondary"
// 																		: c.status === "✅ Enviado"
// 																			? "success.main"
// 																			: "error.main",
// 														}}
// 													>
// 														{c.status}
// 													</TableCell>
// 												</TableRow>
// 											))}
// 									</TableBody>
// 								</Table>
// 							</TableContainer>

// 							<TablePagination
// 								component="div"
// 								count={contacts.length}
// 								page={page}
// 								onPageChange={handleChangePage}
// 								rowsPerPage={rowsPerPage}
// 								onRowsPerPageChange={handleChangeRowsPerPage}
// 								labelRowsPerPage="Linhas por página"
// 							/>

// 							{status && (
// 								<div className="flex items-center">
// 									{isWaiting && (
// 										<div>
// 											<span
// 												className="spinner-grow spinner-grow-sm text-primary ms-2"
// 												role="status"
// 											></span>
// 										</div>
// 									)}
// 									<Typography p={2}>{status}</Typography>
// 								</div>
// 							)}

// 							<Box display="flex" justifyContent="space-between" p={2}>
// 								<Box display="flex" gap={2}>
// 									<Button
// 										variant="contained"
// 										color="primary"
// 										onClick={sendMessages}
// 										disabled={isSending || loading}
// 									>
// 										{isSending ? "Enviando..." : "Enviar Mensagens"}
// 									</Button>
// 									<Button
// 										variant="contained"
// 										color="error"
// 										onClick={cancelSending}
// 										disabled={!isSending}
// 									>
// 										Pausar Envio
// 									</Button>
// 								</Box>

// 								<Button
// 									variant="contained"
// 									color="success"
// 									onClick={exportToExcel}
// 									disabled={contacts.length === 0 || isDelayDisabled || isSending}
// 								>
// 									Salvar Cópia
// 								</Button>
// 							</Box>
// 						</Paper>
// 					)}

// 					<div>
// 						<Alert severity="warning" sx={{ mt: 3 }}>
// 							⚠️ <strong>Atenção:</strong> O envio de mensagens em massa pelo WhatsApp pode violar as
// 							políticas do serviço e resultar no bloqueio temporário ou permanente da sua conta. Use com
// 							responsabilidade e moderação.
// 						</Alert>
// 					</div>

// 					<UploadPhotoV2Image
// 						ident="upload-whatsapp"
// 						nameImg="whatsapp"
// 						id={123}
// 						onChange={({ url }) => setImagePreview(url)}
// 						type="logo"
// 					/>

// 					<Footer />
// 				</Box>
// 			</div>
// 		</>
// 	);
// };

// export default WhatsAppSender;

// import { useState, useRef, useEffect } from "react";
// import * as XLSX from "xlsx";
// import {
// 	Table,
// 	TableBody,
// 	TableCell,
// 	TableContainer,
// 	TableHead,
// 	TableRow,
// 	Paper,
// 	TablePagination,
// 	TextField,
// 	Button,
// 	Typography,
// 	Box,
// 	Alert,
// 	LinearProgress,
// } from "@mui/material";
// import HeaderReport from "../../components/header/report";
// import { getStatus, sendImageMessage, sendMessage } from "./req/reqMessage";
// import Footer from "../../components/footer";
// import reqEmpresaCredenciais from "../../services/reqMultiTenant/reqEmpresaCredenciais";
// import reqWhatsapp from "../../services/reqMultiTenant/reqWhatsApp";
// import { toast, ToastContainer } from "react-toastify";
// import InstanceCard from "../register/whatsapp/form/card";
// import UploadPhotoV2Image from "../../components/uploading/uploadingPhotoV2image";
// import { set } from "date-fns";

// const WhatsAppSender = () => {
// 	const [contacts, setContacts] = useState([]);
// 	// Agora armazenamos as 3 mensagens num array
// 	const [messages, setMessages] = useState(["", "", ""]);
// 	const [status, setStatus] = useState("");
// 	const [loading, setLoading] = useState(true);
// 	const [fields, setFields] = useState([]);
// 	const [page, setPage] = useState(0);
// 	const [rowsPerPage, setRowsPerPage] = useState(50);

// 	//controle de tempo entre mensagens
// 	const [batchSize, setBatchSize] = useState(9); // pausar a cada N mensagens
// 	const [batchPauseMin, setBatchPauseMin] = useState(4); // duração da pausa em minutos
// 	const [delayMin, setDelayMin] = useState(36); // valor padrão 3s
// 	const [delayMax, setDelayMax] = useState(62); // valor padrão 6s
// 	const [isDelayDisabled, setIsDelayDisabled] = useState(false); // Estado para controlar se o delay está desabilitado
// 	const [errorLimit, setErrorLimit] = useState(500);

// 	const [instanciaWhatsapp, setInstanciaWhatsapp] = useState([]);
// 	const [credentials, setCredentials] = useState([]);
// 	const [selectedInstance, setSelectedInstance] = useState(null);

// 	const [imagePreview, setImagePreview] = useState(null);

// 	const [isWaiting, setIsWaiting] = useState(false);

// 	const cancelRef = useRef(false);

// 	// Para focar cada textarea individualmente
// 	const textFieldRefs = [useRef(null), useRef(null), useRef(null)];

// 	const STATUS_CYCLE = ["📤 Pendente Envio", "🚫 Não enviar", "✅ Enviado"];

// 	useEffect(() => {
// 		fetchCredentials();
// 	}, []);

// 	useEffect(() => {
// 		if (instanciaWhatsapp.length > 0 && !selectedInstance) {
// 			const firstWithPhone = instanciaWhatsapp.find((instance) => instance.telefone);
// 			if (firstWithPhone) {
// 				setSelectedInstance(firstWithPhone);
// 			} else {
// 				// Se nenhum tem telefone, pode escolher o primeiro ou deixar null
// 				setSelectedInstance(instanciaWhatsapp[0]);
// 			}
// 		}
// 	}, [instanciaWhatsapp, selectedInstance]);

// 	const fetchCredentials = async () => {
// 		try {
// 			setLoading(true);

// 			const [empresaCredenciaisRes, whatsappRes] = await Promise.all([
// 				reqEmpresaCredenciais.listId("whatsapp"),
// 				reqWhatsapp.listPaginate("descricao", "", "SIM", 1),
// 			]);

// 			setCredentials(empresaCredenciaisRes.data);
// 			setInstanciaWhatsapp(whatsappRes.data);
// 		} catch (error) {
// 			toast.warning(error?.message || "Erro ao buscar credenciais");
// 		} finally {
// 			setLoading(false);
// 		}
// 	};

// 	const handleFileUpload = (e) => {
// 		try {
// 			setLoading(true);
// 			const file = e.target.files[0];
// 			if (!file) return;

// 			const reader = new FileReader();

// 			reader.onload = (event) => {
// 				const data = new Uint8Array(event.target.result);
// 				const workbook = XLSX.read(data, { type: "array" });
// 				const sheetName = workbook.SheetNames[0];
// 				const worksheet = workbook.Sheets[sheetName];
// 				const json = XLSX.utils.sheet_to_json(worksheet);

// 				const formatted = json.map((row) => ({
// 					...row,
// 					status: row.status || "📤 Pendente Envio",
// 				}));

// 				setContacts(formatted);
// 				setStatus(`Carregado ${formatted.length} contatos.`);

// 				if (formatted.length > 0) {
// 					const detectedFields = Object.keys(formatted[0]).filter((key) => key.toLowerCase() !== "status");
// 					setFields(detectedFields);
// 				}
// 			};

// 			reader.readAsArrayBuffer(file);
// 		} finally {
// 			setLoading(false);
// 			// 🔹 Resetar input garante que Chrome detecte reenvio do mesmo arquivo
// 			e.target.value = null;
// 		}
// 	};

// 	const handleChange = (index, field, value) => {
// 		const updated = [...contacts];
// 		updated[index][field] = value;
// 		setContacts(updated);
// 	};

// 	const toggleStatus = (index) => {
// 		const updated = [...contacts];
// 		const current = updated[index].status;
// 		const nextStatus = STATUS_CYCLE[(STATUS_CYCLE.indexOf(current) + 1) % STATUS_CYCLE.length];
// 		updated[index].status = nextStatus;
// 		setContacts(updated);
// 	};

// 	const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
// 	const getRandomDelay = (min = delayMin, max = delayMax) => {
// 		// Garante que min e max sejam no mínimo 3
// 		const minVal = Math.max(3, min);
// 		const maxVal = Math.max(5, max);

// 		// Garante que maxVal seja >= minVal
// 		const finalMin = minVal;
// 		const finalMax = maxVal < minVal ? minVal : maxVal;

// 		// Gera um delay aleatório entre finalMin e finalMax (em segundos) e converte para ms
// 		return Math.floor(Math.random() * (finalMax - finalMin + 1) + finalMin) * 1000;
// 	};
// 	// const getRandomDelay = (min = delayMin * 1000, max = delayMax * 1000) =>
// 	// 	Math.floor(Math.random() * (max - min + 1)) + min;

// 	const sendMessages = async () => {
// 		let countError = 0;
// 		try {
// 			setIsDelayDisabled(true); // Desabilita os campos de delay durante o envio
// 			const cred = await getCredentials();

// 			// Filtra apenas mensagens preenchidas
// 			const validMessages = messages.filter((msg) => msg.trim() !== "");

// 			if (validMessages.length === 0) {
// 				const msg = "Por favor, preencha pelo menos uma mensagem.";
// 				toast.warning(msg);
// 				setStatus(msg);
// 				return;
// 			}

// 			if (contacts.length === 0) {
// 				const msg = "Por favor, carregue contatos.";
// 				toast.warning(msg);
// 				setStatus(msg);
// 				return;
// 			}

// 			setLoading(true);
// 			setStatus("🚀 Iniciando envio das mensagens...");
// 			cancelRef.current = false;

// 			const updatedContacts = [...contacts];

// 			let sentInCurrentBatch = 0;

// 			for (let i = 0; i < updatedContacts.length; i++) {
// 				// Sorteia uma mensagem diferente para cada contato
// 				const messageToSend = validMessages[Math.floor(Math.random() * validMessages.length)];

// 				if (countError >= Number(errorLimit)) {
// 					setStatus("❌ Muitos erros consecutivos. Envio interrompido.");
// 					toast.error("Muitos erros consecutivos. Envio interrompido.");
// 					break;
// 				}

// 				if (cancelRef.current) {
// 					setStatus("⛔ Envio cancelado pelo usuário.");
// 					break;
// 				}

// 				const contact = updatedContacts[i];

// 				if (contact.status !== "📤 Pendente Envio") continue;

// 				const phoneField =
// 					Object.keys(contact).find((key) => key.toLowerCase().includes("telefone")) || "telefone";

// 				const personalizedMessage = Object.keys(contact).reduce(
// 					(msg, field) => msg.replace(new RegExp(`{${field}}`, "gi"), contact[field] || ""),
// 					messageToSend,
// 				);

// 				try {
// 					if (imagePreview) {
// 						await sendImageMessage(
// 							cred.url,
// 							cred.apiKey,
// 							cred.instance,
// 							contact[phoneField],
// 							imagePreview,
// 							personalizedMessage,
// 						);
// 					} else
// 						await sendMessage(
// 							cred.url,
// 							cred.apiKey,
// 							cred.instance,
// 							contact[phoneField],
// 							personalizedMessage,
// 						);

// 					setStatus(`📨 Enviando para ${contact.nome || "Sem nome"} (${contact[phoneField]})...`);

// 					updatedContacts[i].status = "✅ Enviado";
// 					countError = 0;
// 				} catch (error) {
// 					updatedContacts[i].status = "❌ Falhou";
// 					countError++;
// 				}

// 				setContacts([...updatedContacts]);

// 				if (i < updatedContacts.length - 1) {
// 					sentInCurrentBatch++;

// 					// precisa pausar?
// 					const needsBatchPause =
// 						Number(batchSize) > 0 && Number(batchPauseMin) > 0 && sentInCurrentBatch >= Number(batchSize);

// 					if (needsBatchPause) {
// 						// PAUSA POR LOTE
// 						const pauseMs = Number(batchPauseMin) * 60 * 1000;

// 						setIsWaiting(true);

// 						const startedAt = Date.now();
// 						let remaining = pauseMs;

// 						// contador simples de 1s para atualizar o status e permitir cancelamento
// 						while (remaining > 0) {
// 							if (cancelRef.current) {
// 								setStatus("⛔ Envio cancelado pelo usuário durante a pausa.");
// 								setIsWaiting(false);
// 								return; // sai de toda a função sendMessages
// 							}

// 							const mins = Math.floor(remaining / 60000);
// 							const secs = Math.ceil((remaining % 60000) / 1000);
// 							setStatus(`⏸️ Pausa automática: retoma em ${mins}m ${secs}s...`);

// 							await delay(1000);
// 							remaining = pauseMs - (Date.now() - startedAt);
// 						}

// 						setIsWaiting(false);
// 						sentInCurrentBatch = 0; // reinicia o lote após a pausa
// 					} else {
// 						try {
// 							const waitTime = getRandomDelay();
// 							setIsWaiting(true);
// 							setStatus(`Aguardando ${waitTime / 1000}s antes da próxima mensagem...`);
// 							await delay(waitTime);
// 						} finally {
// 							setIsWaiting(false);
// 						}
// 					}
// 				}
// 			}

// 			setLoading(false);
// 			setStatus("✅ Envio de mensagens finalizado.");
// 		} catch (error) {
// 			toast.error(error.message);
// 			setStatus(`❌ Erro: ${error.message}`);
// 			setLoading(false);
// 		} finally {
// 			setIsDelayDisabled(false);
// 		}
// 	};

// 	const cancelSending = () => {
// 		cancelRef.current = true;
// 		setStatus("Pausa envio...");
// 	};

// 	const exportToExcel = () => {
// 		const worksheet = XLSX.utils.json_to_sheet(contacts);
// 		const workbook = XLSX.utils.book_new();
// 		XLSX.utils.book_append_sheet(workbook, worksheet, "Contatos");

// 		XLSX.writeFile(workbook, "envio-whatsapp.xlsx");
// 		setStatus("Arquivo salvo.");
// 	};

// 	const handleChangePage = (event, newPage) => setPage(newPage);
// 	const handleChangeRowsPerPage = (event) => {
// 		setRowsPerPage(parseInt(event.target.value, 10));
// 		setPage(0);
// 	};

// 	// Função para inserir texto com foco na textarea específica
// 	const insertText = (text, index) => {
// 		setMessages((prev) => {
// 			const updated = [...prev];
// 			updated[index] = updated[index] + text + " ";
// 			return updated;
// 		});
// 		setTimeout(() => {
// 			if (textFieldRefs[index].current) {
// 				textFieldRefs[index].current.focus();
// 			}
// 		}, 0);
// 	};

// 	const handleOpenUploadComponent = (e) => {
// 		e.preventDefault();

// 		const event = new CustomEvent("upload-whatsapp");
// 		window.dispatchEvent(event);
// 	};

// 	const getCredentials = async () => {
// 		try {
// 			const apiKey = credentials.api_key;
// 			const url = credentials.url;
// 			const instance = selectedInstance.descricao;

// 			// Verificar status da instância antes de retornar
// 			const statusData = await getStatus(url, apiKey, instance);

// 			if (!statusData) {
// 				throw new Error("Não foi possível verificar o status da instância.");
// 			}

// 			if (statusData.status === "DISCONNECTED") {
// 				throw new Error(`A instância "${instance}" está desconectada.`);
// 			}

// 			if (statusData.status === "NOT_FOUND") {
// 				throw new Error(`A instância "${instance}" não existe.`);
// 			}

// 			return { apiKey, url, instance };
// 		} catch (error) {
// 			console.error("Erro ao obter credenciais:", error);
// 			throw error;
// 		}
// 	};

// 	return (
// 		<>
// 			<ToastContainer />
// 			<div className="mt-page" mensagem="Carregando...">
// 				<Box maxWidth="lg" mx="auto">
// 					{/* <div className="grid grid-cols-1 md:grid-cols-2 gap-4"> */}
// 					<div className="border-bottom mb-4 mt-4">
// 						<div className="flex flex-col gap-4">
// 							<HeaderReport title={"WhatsApp"} subTitle={"Envio de lista"} />
// 						</div>

// 						<div className="flex flex-col justify-start items-start w-full md:w-auto">
// 							{selectedInstance && (
// 								<InstanceCard
// 									instanceName={selectedInstance?.descricao || ""}
// 									instancePhone={selectedInstance?.telefone || ""}
// 								/>
// 							)}
// 							<div className="mt-1 input-select-box w-60 flex flex-col">
// 								<label className="text-sm">Instância WhatsApp</label>
// 								<select
// 									value={selectedInstance ? JSON.stringify(selectedInstance) : ""}
// 									onChange={(e) => {
// 										const value = e.target.value;
// 										if (!value) return; //
// 										setSelectedInstance(value ? JSON.parse(value) : null);
// 									}}
// 									className="border rounded p-2 w-full text-sm leading-tight"
// 								>
// 									<option value="">Selecione a instância</option>
// 									{instanciaWhatsapp.filter(Boolean).map((instance, index) => (
// 										<option
// 											key={instance.id || `${instance.telefone}-${index}`}
// 											value={JSON.stringify(instance)}
// 											className="text-sm" // 👈 também nas opções
// 										>
// 											{instance.telefone
// 												? `${instance.telefone} - ${instance.descricao}`
// 												: `${instance.descricao} - Instância não criada`}
// 										</option>
// 									))}
// 								</select>
// 							</div>
// 						</div>
// 					</div>

// 					{/* --- Três caixas de mensagem com botão de clonar --- */}

// 					<Box display="flex" className="overflow-auto" gap={3} mb={1}>
// 						{[0, 1, 2].map((idx) => (
// 							<Box key={idx} flex={1}>
// 								<Typography variant="subtitle1" gutterBottom>
// 									Mensagem {idx + 1}:
// 								</Typography>
// 								<TextField
// 									inputRef={textFieldRefs[idx]}
// 									multiline
// 									rows={14}
// 									value={messages[idx]}
// 									onChange={(e) => {
// 										const updated = [...messages];
// 										updated[idx] = e.target.value;
// 										setMessages(updated);
// 									}}
// 									placeholder={`Digite a mensagem ${idx + 1}`}
// 									fullWidth
// 								/>
// 								<Box mt={1} display="flex" flexWrap="wrap" gap={1}>
// 									<Button size="small" onClick={() => insertText("*texto*", idx)}>
// 										*Negrito*
// 									</Button>
// 									<Button size="small" onClick={() => insertText("_texto_", idx)}>
// 										_Itálico_
// 									</Button>
// 									<Button size="small" onClick={() => insertText("~texto~", idx)}>
// 										~Riscado~
// 									</Button>
// 									<Button size="small" onClick={() => insertText("`texto`", idx)}>
// 										`Monoespaçado`
// 									</Button>
// 									{fields.map((field) => (
// 										<Button key={field} size="small" onClick={() => insertText(`{${field}}`, idx)}>
// 											{`{${field}}`}
// 										</Button>
// 									))}
// 								</Box>
// 							</Box>
// 						))}
// 					</Box>

// 					{/* Botão para clonar mensagem 1 para 2 e 3 */}
// 					<Box className="flex justify-between flex-wrap" mb={3}>
// 						<Box display="flex" gap={2} alignItems="center" mb={2}>
// 							<TextField
// 								label="Delay mínimo em segundos"
// 								type="number"
// 								value={delayMin}
// 								onChange={(e) => setDelayMin(Number(e.target.value))}
// 								size="small"
// 								inputProps={{ min: 3 }}
// 								disabled={isDelayDisabled}
// 							/>
// 							<TextField
// 								label="Delay máximo em segundos"
// 								type="number"
// 								value={delayMax}
// 								onChange={(e) => setDelayMax(Number(e.target.value))}
// 								size="small"
// 								inputProps={{ min: delayMin }}
// 								disabled={isDelayDisabled}
// 							/>
// 						</Box>
// 						<Box display="flex" gap={2} alignItems="center" mb={2}>
// 							<TextField
// 								label="Pausar após (qtd. de mensagens)"
// 								type="number"
// 								value={batchSize}
// 								onChange={(e) => setBatchSize(Math.max(1, Number(e.target.value)))}
// 								size="small"
// 								inputProps={{ min: 10 }}
// 								disabled={isDelayDisabled}
// 							/>
// 							<TextField
// 								label="Tempo da pausa (minutos)"
// 								type="number"
// 								value={batchPauseMin}
// 								onChange={(e) => setBatchPauseMin(Math.max(1, Number(e.target.value)))}
// 								size="small"
// 								inputProps={{ min: 1 }}
// 								disabled={isDelayDisabled}
// 							/>
// 						</Box>
// 						<Box display="flex" gap={2} alignItems="center" mb={2}>
// 							<TextField
// 								label="Máx. erros consecutivos"
// 								type="number"
// 								value={errorLimit}
// 								onChange={(e) => setErrorLimit(Math.max(1, Number(e.target.value)))}
// 								size="small"
// 								inputProps={{ min: 1 }}
// 								disabled={isDelayDisabled}
// 							/>
// 						</Box>
// 						<Button
// 							variant="contained"
// 							className="h-8"
// 							onClick={() => {
// 								setMessages((prev) => [prev[0], prev[0], prev[0]]);
// 								setTimeout(() => {
// 									if (textFieldRefs[1].current) textFieldRefs[1].current.focus();
// 								}, 0);
// 							}}
// 						>
// 							Clonar mensagem
// 						</Button>
// 					</Box>

// 					<div>
// 						{imagePreview && (
// 							<div className="relative h-72 rounded-lg overflow-hidden ">
// 								<img
// 									src={imagePreview}
// 									alt=""
// 									className="absolute inset-0 w-full h-full object-contain rounded-lg shadow-lg"
// 								/>
// 							</div>
// 						)}

// 						<button
// 							className="btn btn-sm btn-outline-primary mt-3 w-56 me-5"
// 							onClick={handleOpenUploadComponent}
// 						>
// 							Escolher imagem
// 						</button>
// 						{/* <ImagemPreview imagePreview={imagePreview} setImagePreview={setImagePreview} /> */}

// 						<button
// 							className="btn btn-sm btn-outline-danger mt-3 w-56"
// 							onClick={() => setImagePreview(null)}
// 							hidden={!imagePreview}
// 						>
// 							Limpar imagem
// 						</button>
// 					</div>
// 					<Button variant="contained" className="w-48 h-8 mt-4" component="label">
// 						Carregar Planilha
// 						<input type="file" accept=".xlsx, .xls" hidden onChange={handleFileUpload} />
// 					</Button>
// 					{contacts.length > 0 && (
// 						<Paper sx={{ mt: 3 }}>
// 							<Typography variant="h6" p={2}>
// 								Contatos
// 							</Typography>

// 							<TableContainer>
// 								<Table>
// 									<TableHead className="color-table">
// 										<TableRow>
// 											{Object.keys(contacts[0])
// 												.filter((col) => col !== "status")
// 												.map((col) => (
// 													<TableCell key={col} style={{ color: "#fff" }}>
// 														{col.toUpperCase()}
// 													</TableCell>
// 												))}
// 											<TableCell style={{ color: "#fff" }}>Status</TableCell>
// 										</TableRow>
// 									</TableHead>
// 									{loading && (
// 										<TableRow>
// 											<TableCell colSpan={Object.keys(contacts[0]).length} sx={{ p: 0 }}>
// 												<LinearProgress color="primary" />
// 											</TableCell>
// 										</TableRow>
// 									)}

// 									<TableBody>
// 										{contacts
// 											.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
// 											.map((c, i) => (
// 												<TableRow key={i}>
// 													{Object.keys(c)
// 														.filter((col) => col !== "status")
// 														.map((col) => (
// 															<TableCell key={col}>
// 																<TextField
// 																	value={c[col]}
// 																	onChange={(e) =>
// 																		handleChange(
// 																			page * rowsPerPage + i,
// 																			col,
// 																			e.target.value,
// 																		)
// 																	}
// 																	size="small"
// 																	fullWidth
// 																/>
// 															</TableCell>
// 														))}
// 													<TableCell
// 														onClick={() => toggleStatus(page * rowsPerPage + i)}
// 														sx={{
// 															cursor: "pointer",
// 															color:
// 																c.status === "📤 Pendente Envio"
// 																	? "primary.main"
// 																	: c.status === "🚫 Não enviar"
// 																		? "text.secondary"
// 																		: c.status === "✅ Enviado"
// 																			? "success.main"
// 																			: "error.main",
// 														}}
// 													>
// 														{c.status}
// 													</TableCell>
// 												</TableRow>
// 											))}
// 									</TableBody>
// 								</Table>
// 							</TableContainer>

// 							<TablePagination
// 								component="div"
// 								count={contacts.length}
// 								page={page}
// 								onPageChange={handleChangePage}
// 								rowsPerPage={rowsPerPage}
// 								onRowsPerPageChange={handleChangeRowsPerPage}
// 								labelRowsPerPage="Linhas por página"
// 							/>
// 							{status && (
// 								<div className="flex items-center">
// 									{isWaiting && (
// 										<div>
// 											{/* <span className="spinner-grow spinner-grow-sm me-2" role="status"></span> */}
// 											<span
// 												className="spinner-grow spinner-grow-sm text-primary ms-2"
// 												role="status"
// 											></span>
// 										</div>
// 									)}
// 									<Typography p={2}>{status}</Typography>
// 								</div>
// 							)}

// 							<Box display="flex" justifyContent="space-between" p={2}>
// 								<Box display="flex" gap={2}>
// 									<Button
// 										variant="contained"
// 										color="primary"
// 										onClick={sendMessages}
// 										disabled={loading}
// 									>
// 										{loading ? "Enviando..." : "Enviar Mensagens"}
// 									</Button>
// 									<Button
// 										variant="contained"
// 										color="error"
// 										onClick={cancelSending}
// 										disabled={!loading}
// 									>
// 										Pausar Envio
// 									</Button>
// 								</Box>
// 								<Button
// 									variant="contained"
// 									color="success"
// 									onClick={exportToExcel}
// 									disabled={contacts.length === 0 || isDelayDisabled}
// 								>
// 									Salvar Cópia
// 								</Button>
// 							</Box>
// 						</Paper>
// 					)}

// 					<div>
// 						<Alert severity="warning" sx={{ mt: 3 }}>
// 							⚠️ <strong>Atenção:</strong> O envio de mensagens em massa pelo WhatsApp pode violar as
// 							políticas do serviço e resultar no bloqueio temporário ou permanente da sua conta. Use com
// 							responsabilidade e moderação.
// 						</Alert>
// 					</div>
// 					<UploadPhotoV2Image
// 						ident="upload-whatsapp"
// 						nameImg="whatsapp"
// 						id={123}
// 						onChange={({ url }) => setImagePreview(url)}
// 						type="logo"
// 					/>
// 					<Footer />
// 				</Box>
// 			</div>
// 		</>
// 	);
// };

// export default WhatsAppSender;
